import { useState, useMemo, useRef, useEffect, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOutsideClick, useThrottle } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { Icon, SearchInput, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface SelectProps<T> {
  className?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
  label?: ReactNode;
  isLoading?: boolean;
  enableSearch?: boolean;
  items: { label: string; value: T }[] | string[];
  nullValue?: { isEnable: boolean; text?: string };
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  value?: T | string;
  onChange: (value?: T) => void;
}

export function Select<T>(props: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const selectRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation();

  const updateMenuPosition = useCallback(() => {
    if (!selectRef.current) return;

    const rect = selectRef.current.getBoundingClientRect();
    setMenuStyles({
      width: rect.width,
      position: 'absolute',
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, [selectRef.current]);

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  const throttledUpdateMenuPosition = useThrottle(updateMenuPosition, 10);
  useEventListener('scroll', throttledUpdateMenuPosition, undefined, true);
  useEventListener('resize', throttledUpdateMenuPosition);
  useOutsideClick([selectRef, menuRef], () => setIsOpen(false));

  const defaultNullValueText = t('placeholders.all');
  const nullText = `${props.nullValue?.text || defaultNullValueText}${props.required ? ' *' : ''}`;
  const currentLabel = useMemo(() => {
    if (props.isLoading) return t('select.loading');
    if (props.items.length === 0) return undefined;
    if (typeof props.items[0] === 'string') return props.value as string;
    return (props.items as { label: string; value: T }[]).find((x) => x.value === props.value)?.label;
  }, [props.value, props.items, props.isLoading]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return props.items;
    return props.items.filter((item) => {
      const label = typeof item === 'string' ? item : item.label;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, props.items]);

  return (
    <div
      ref={selectRef}
      className={cn(
        'relative flex w-auto flex-col',
        props.disabled && 'pointer-events-none opacity-60',
        props.readOnly && 'pointer-events-none',
        props.className,
      )}
    >
      {props.label && (
        <label className='w-full'>
          <Text size='sm' color='secondary'>
            {props.label} {props.required ? ' *' : ''}
          </Text>
        </label>
      )}
      <div
        className={cn(
          'flex h-10 w-full cursor-pointer transition-all duration-200',
          PaletteUtils.isInsigniaPalette && [
            'border border-transparent border-b-light bg-ice dark:bg-soft-black',
            'hover:border hover:border-primary hover:bg-white',
          ],
          PaletteUtils.isVcfcPalette && [
            'h-12 rounded-[100px] border border-light/70 bg-white dark:border-transparent dark:bg-white/10',
            'hover:border-primary dark:hover:border-light/70',
          ],
          props.wrapperClassName,
        )}
        onClick={() => {
          if (props.disabled || props.readOnly || props.isLoading) return;
          setIsOpen(!isOpen);
        }}
      >
        <div className={cn('flex w-full items-center justify-between gap-2 px-2 py-2', PaletteUtils.isVcfcPalette && 'px-4')}>
          <p
            className={cn(
              'w-full text-nowrap text-left font-light leading-normal',
              PaletteUtils.isVcfcPalette && 'text-dark-wet-asphalt dark:text-white/70',
              PaletteUtils.isInsigniaPalette && 'text-dark-wet-asphalt dark:text-gray-dark/70',
              props.value && 'text-soft-black dark:text-white',
            )}
          >
            {currentLabel || nullText}
          </p>
          <div
            className={cn(
              'flex items-center justify-center',
              PaletteUtils.isInsigniaPalette && 'relative left-[9px] h-10 w-10 shrink-0 bg-soft-black dark:bg-slate',
              props.buttonClassName,
            )}
          >
            <Icon
              icon={<ChevronIcon />}
              className={cn('-rotate-90 transition-transform duration-200', isOpen && 'rotate-90', props.iconClassName)}
              color={PaletteUtils.getColor('primary', PaletteUtils.isVcfcPalette ? 'dark' : 'white')}
            />
          </div>
        </div>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(
              'absolute z-[99] flex max-h-40 w-full flex-col overflow-hidden border bg-white dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && 'rounded-2xl border-light/70',
            )}
            style={menuStyles}
          >
            <div className={cn('relative h-full overflow-y-auto', PaletteUtils.isVcfcPalette && ['py-2', props.enableSearch && 'pt-0'])}>
              {props.enableSearch && (
                <div className='sticky left-0 top-0'>
                  <SearchInput ref={searchInputRef} className='w-full' inputClassName='rounded-[14px]' value={searchTerm} onChange={setSearchTerm} />
                </div>
              )}
              {props.nullValue?.isEnable && (
                <SelectItem
                  label={props.nullValue.text ?? defaultNullValueText}
                  isSelected={!props.value || (Array.isArray(props.value) && props.value.length === 0)}
                  onChange={() => {
                    props.onChange(undefined);
                    setIsOpen(false);
                  }}
                />
              )}
              {filteredItems.length === 0 ? (
                <div className='flex h-8 items-center justify-center bg-white dark:bg-soft-black'>
                  <p className='font-light text-soft-black opacity-60 dark:text-white'>{t('select.noOptions')}</p>
                </div>
              ) : (
                filteredItems.map((x, key) => {
                  const label = (x as any).label ?? x;
                  const value = (x as any).value ?? x;
                  return (
                    <SelectItem<T>
                      key={key}
                      value={value}
                      label={label}
                      isSelected={props.value === value}
                      onChange={() => {
                        props.onChange(value);
                        setIsOpen(false);
                      }}
                    />
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface ISelectItemProps<T> {
  value?: T;
  label: string;
  isSelected: boolean;
  onChange: () => void;
}

function SelectItem<T>(props: ISelectItemProps<T>) {
  return (
    <div
      onClick={props.onChange}
      className={cn(
        'z-10 flex cursor-pointer bg-white dark:bg-soft-black',
        PaletteUtils.isInsigniaPalette && ['hover:bg-gray-silk dark:hover:bg-olive-gray', props.isSelected && 'bg-gray-silk dark:bg-olive-gray'],
        PaletteUtils.isVcfcPalette && ['hover:bg-primary-lightest dark:hover:bg-slate', props.isSelected && 'bg-primary-lightest dark:bg-slate'],
      )}
    >
      <p
        className={cn(
          'overflow-hidden text-ellipsis px-2 py-2 text-center font-light leading-normal',
          PaletteUtils.isVcfcPalette && ['px-4 text-soft-black dark:text-white/50', props.isSelected && 'text-primary dark:text-white'],
          PaletteUtils.isInsigniaPalette && ['text-soft-black dark:text-white', props.isSelected && 'text-primary dark:text-white'],
        )}
      >
        {props.label}
      </p>
    </div>
  );
}
